import * as React from 'react'
import { useParams } from 'react-router-dom'
import clx from 'classnames'
import dayjs from 'dayjs'
import { useAuth } from 'react-oidc-context'
import { ColouredSuccesCheckIcon } from '@/app/features/icons/ColouredSuccesCheck'
import { DashboardStepper } from './DashboardStepper'
import { Button } from '@/app/features/common/Button'
import { ColouredImportIcon } from '@/app/features/icons/ColouredImport'
import { ArrowRightIcon } from '@/app/features/icons/ArrowRight'
import { RoundedImportIcon } from '@/app/features/icons/RoundedImport'
import { HOME_ROUTES } from '@/modules/home/routes-typed'
import { useQueryAccount } from '@/modules/shared/query-client'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { SkeletonBox } from '@/app/features/common/SkeletonBox'
import { Loader } from '@/ui'

import { DashboardData } from '../types'

type Props = {
  stats?: DashboardData['imports']
  importData: () => void
  selectedMonth: dayjs.Dayjs
  className?: string
  isLoading?: boolean
  isRefreshing?: boolean
}


const AvatarCardStatsSkeleton = () => (
  <>
    <div className="tw-flex tw-flex-col tw-gap-4">
      <SkeletonBox className="tw-h-4 tw-w-44" />
      <div className="tw-relative tw-flex tw-flex-col tw-gap-3">
        {[0, 1].map((row) => (
          <div key={row} className="tw-flex tw-items-start tw-gap-2.5">
            <SkeletonBox className="tw-mt-0.5 tw-h-4 tw-w-4 tw-shrink-0 tw-rounded-full" />
            <div className="tw-flex tw-flex-col tw-gap-1.5">
              <SkeletonBox className="tw-h-3.5 tw-w-32" />
              <SkeletonBox className="tw-h-4 tw-w-24" />
            </div>
          </div>
        ))}
      </div>
    </div>
    <div className="tw-mt-6 tw-flex tw-flex-col tw-gap-3">
      <SkeletonBox className="tw-h-10 tw-w-full tw-rounded-lg" />
    </div>
  </>
)

const DashboardAvatar = () => (
  <div className="tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center tw-rounded-full">
    <svg
      viewBox="0 0 64 64"
      className="tw-h-[64px] tw-w-[64px] tw-text-content-base"
      fill="none"
      aria-label="User avatar"
    >
      <circle cx="32" cy="32" r="30" stroke="currentColor" strokeWidth="2.5" />
      <circle cx="32" cy="24" r="10" stroke="currentColor" strokeWidth="2.5" />
      <path
        d="M14 50c3.5-8 10.5-12 18-12s14.5 4 18 12"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
    </svg>
  </div>
)

export const AvatarCard = ({
  stats,
  importData,
  selectedMonth,
  className,
  isLoading,
  isRefreshing,
}: Props) => {
  const { companyId } = useParams<{ companyId: string }>()
  const { t } = useLocalize()
  const auth = useAuth()
  const userUniqueId = auth.isAuthenticated ? auth.user?.profile.sub || '' : ''
  const { data: accountData } = useQueryAccount({ userUniqueId })

  const personData = accountData as
    | {
        firstName?: string
        lastName?: string
        surname?: string
        login?: string
      }
    | undefined
  const greetingName =
    personData?.firstName?.trim().split(/\s+/)[0] || personData?.login || ''

  const selectedMonthParam = selectedMonth.format('YYYY-MM')
  const settledDriversCount = Number(stats?.settledDrivers) || 0
  const importedDriversCount = Number(stats?.importedDrivers) || 0
  const totalDriversCount = Number(stats?.totalDrivers) || 0
  const settlementTotalCount = Number(stats?.driversWithContract) || 0
  const normalizedImportedDriversCount = Math.min(
    totalDriversCount,
    importedDriversCount,
  )

  const isMonthSettleable = selectedMonth.endOf('month').isBefore(dayjs())
  const isFullySettled =
    settlementTotalCount > 0 && settledDriversCount >= settlementTotalCount
  const isFullyImported =
    totalDriversCount > 0 && normalizedImportedDriversCount >= totalDriversCount
  const isFinalizedView =
    isMonthSettleable && !!stats?.finalizedAt && isFullySettled

  const shouldShowSettlementProgress =
    isMonthSettleable && settledDriversCount > 0 && !isFinalizedView

  const canFinalizeNow =
    isMonthSettleable && !isFinalizedView && !!stats?.canFinalize

  const shouldShowImportPrompt =
    !isMonthSettleable ||
    (!shouldShowSettlementProgress &&
      !isFullyImported &&
      !isFinalizedView &&
      !canFinalizeNow)

  // Rozliczenie miesiąca może obejmować pierwsze dni następnego miesiąca
  // (np. maj do 3 czerwca) — wtedy import nadal powinien być dostępny.
  const SETTLEMENT_IMPORT_EXTENSION_DAYS = 3
  const importExtensionEnd = selectedMonth
    .add(1, 'month')
    .startOf('month')
    .add(SETTLEMENT_IMPORT_EXTENSION_DAYS - 1, 'day')
    .endOf('day')
  const isWithinImportExtensionPeriod =
    dayjs().isAfter(selectedMonth.endOf('month')) &&
    !dayjs().isAfter(importExtensionEnd)

  const canShowImportButton =
    isMonthSettleable &&
    !isFinalizedView &&
    (!isFullyImported || isWithinImportExtensionPeriod)
  const canShowFinishSettlementButton = shouldShowSettlementProgress
  const canShowSettleButton = canFinalizeNow && settledDriversCount === 0
  const availableDate = selectedMonth
    .add(1, 'month')
    .startOf('month')
    .format('D.MM.YY')

  const finalizedAt = dayjs(stats?.finalizedAt).format('D.MM.YY')
  const settlementMonth = `${selectedMonth.format('MMMM')} ${selectedMonth.format('YYYY')}`

  return (
    <div
      className={clx(
        'tw-relative tw-flex tw-flex-col tw-gap-8 tw-border-b tw-border-r-base-300 tw-bg-primary-linear tw-p-6 md:tw-h-full md:tw-flex-row md:tw-gap-16 xl:tw-w-[286px] xl:tw-flex-col xl:tw-gap-8 xl:tw-border-l-0 xl:tw-border-r xl:tw-border-b-transparent xl:tw-pb-6 xl:tw-pt-14',
        {
          'tw-bg-success-linear': isFinalizedView,
        },
        className,
      )}
    >
      {isRefreshing && (
        <Loader
          position="absolute"
          className="tw-inset-0 tw-z-10 tw-bg-base-100/60 tw-backdrop-blur-[1px]"
          size="sm"
        />
      )}
      <div className="tw-flex tw-flex-col tw-gap-6">
        <span className="tw-h-[72px] tw-w-[72px]">
          <DashboardAvatar />
        </span>
        <p className="tw-mb-0 tw-flex tw-min-w-0 tw-max-w-full tw-gap-1 tw-text-xl">
          <span className="tw-shrink-0">{t('dashboard.greeting')},</span>
          <span className="tw-min-w-0 tw-flex-1 tw-truncate tw-font-semibold">
            {greetingName || t('dashboard.user')}!
          </span>
        </p>
      </div>
      <div>
        {isLoading ? (
          <AvatarCardStatsSkeleton />
        ) : isFinalizedView ? (
          <div className="tw-flex tw-flex-col tw-gap-6 tw-text-sm tw-text-content-base/80">
            <span className="tw-flex tw-h-[60px] tw-w-[60px] tw-items-center tw-justify-center tw-rounded-[20px] tw-bg-content-success">
              <ColouredSuccesCheckIcon />
            </span>
            <div className="tw-pl-2">
              <DashboardStepper
                totalDrivers={stats?.totalDrivers}
                settlementTotalDrivers={settlementTotalCount}
                finalizedAt={finalizedAt}
                progressAmount={settledDriversCount}
                driversWithPrematureRead={stats?.driversWithPrematureRead}
                selectedMonth={selectedMonth}
              />
            </div>
          </div>
        ) : (
          <>
            <div>
              {shouldShowImportPrompt ? (
                <div className="tw-flex tw-flex-col tw-gap-6 tw-text-sm tw-text-content-base/80">
                  <span className="tw-flex tw-h-[60px] tw-w-[60px] tw-items-center tw-justify-center tw-rounded-[20px] tw-bg-content-neutral">
                    <ColouredImportIcon />
                  </span>
                  <div className="md:tw-max-w-92">
                    <p className="tw-mb-2 tw-text-lg tw-font-semibold tw-text-content-base">
                      {t('dashboard.import-data')}
                    </p>
                    <p className="tw-mb-0 tw-text-sm tw-text-content-base/80">
                      {t('dashboard.import-data-prompt')}{' '}
                      <span className="tw-font-medium">{settlementMonth}</span>
                      {!isMonthSettleable && (
                        <>
                          {' '}
                          {t('dashboard.available-from')}{' '}
                          <span className="tw-font-medium">
                            {availableDate}
                          </span>
                        </>
                      )}
                    </p>
                  </div>
                </div>
              ) : shouldShowSettlementProgress ? (
                <div className="tw-flex tw-flex-col tw-gap-4 tw-text-sm tw-text-content-base/80">
                  <p className="tw-mb-0">
                    {t('dashboard.settlement')}{' '}
                    <span className="tw-ml-0.5 tw-font-semibold">
                      {settlementMonth}
                    </span>
                    :
                  </p>
                  <div className="tw-relative tw-flex tw-flex-col tw-gap-3">
                    <span className="tw-absolute tw-left-[7px] tw-top-5 tw-h-8 tw-border-l tw-border-focus-100" />
                    <div className="tw-flex tw-items-start tw-gap-2.5">
                      <span className="tw-mt-0.5 tw-flex tw-h-4 tw-w-4 tw-shrink-0 tw-rounded-full tw-border tw-border-focus-100 tw-bg-content-primary" />
                      <div>
                        <p className="tw-mb-0">
                          {t('dashboard.data-imported')}
                        </p>
                        <p className="tw-mb-0 tw-font-medium">
                          {normalizedImportedDriversCount}/{totalDriversCount}{' '}
                          {t('dashboard.drivers-count')}
                        </p>
                      </div>
                    </div>
                    <div className="tw-flex tw-items-start tw-gap-2.5">
                      <span className="tw-mt-0.5 tw-flex tw-h-4 tw-w-4 tw-shrink-0 tw-rounded-full tw-border tw-border-focus-100 tw-bg-content-primary" />
                      <div>
                        <p className="tw-mb-0">{t('dashboard.settled')}</p>
                        <p className="tw-mb-0 tw-font-medium">
                          {settledDriversCount}/{settlementTotalCount}{' '}
                          {t('dashboard.drivers-count')}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="tw-flex tw-flex-col tw-gap-4 tw-text-sm tw-text-content-base/80">
                  <p className="tw-mb-0">
                    {t('dashboard.settlement')}{' '}
                    <span className="tw-ml-0.5 tw-font-semibold">
                      {settlementMonth}
                    </span>
                    :
                  </p>
                  <DashboardStepper
                    totalDrivers={stats?.totalDrivers}
                    progressAmount={normalizedImportedDriversCount}
                    selectedMonth={selectedMonth}
                  />
                </div>
              )}
            </div>
            <div className="tw-mt-6 tw-flex tw-flex-col tw-gap-3">
              {canShowFinishSettlementButton ? (
                <Button
                  className="tw-h-10 tw-w-full tw-rounded-lg tw-py-3 !tw-text-white hover:!tw-text-white focus:!tw-text-white active:!tw-text-white"
                  as="a"
                  href={`${HOME_ROUTES.creator.buildPath({ companyId: companyId })}?month=${selectedMonthParam}`}
                >
                  {t('dashboard.finish-settlement')}
                  <ArrowRightIcon />
                </Button>
              ) : canShowSettleButton ? (
                <Button
                  className="tw-h-10 tw-w-full tw-rounded-lg tw-py-3 !tw-text-white hover:!tw-text-white focus:!tw-text-white active:!tw-text-white"
                  as="a"
                  href={`${HOME_ROUTES.creator.buildPath({ companyId: companyId })}?month=${selectedMonthParam}`}
                >
                  {t('dashboard.settle')} {settlementMonth.slice(0, -4)}
                  <ArrowRightIcon />
                </Button>
              ) : null}
              {canShowImportButton ? (
                <Button
                  variant={
                    canShowFinishSettlementButton || canShowSettleButton
                      ? 'secondary'
                      : 'primary'
                  }
                  className={clx(`tw-h-10 tw-w-full tw-rounded-lg tw-py-3`, {
                    'tw-text-content-secondary hover:tw-border-transparent hover:tw-bg-secondary':
                      canShowFinishSettlementButton || canShowSettleButton,
                  })}
                  onClick={importData}
                >
                  {t('dashboard.import-data')} <RoundedImportIcon />
                </Button>
              ) : null}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
